import _ from "lodash";
import { action, computed, extendObservable, runInAction } from "mobx";
import Groups from "../constants/Groups";
import { RouteConstants } from "../constants/RouteConstants";
import MLBTrackingAccuracyReportContainer from "../containers/MLBTrackingAccuracyReportContainer";
import MILBTrackingAccuracyReportContainer from "../containers/MILBTrackingAccuracyReportContainer";
import AtlanticTrackingAccuracyReportContainer from "../containers/AtlanticTrackingAccuracyReportContainer";
import AFLTrackingAccuracyReportContainer from "../containers/AFLTrackingAccuracyReportContainer";
import NonMLBTrackingAccuracyReportContainer from "../containers/NonMLBTrackingAccuracyReportContainer";
import FSLTrackingAccuracyReportContainer from "../containers/FSLTrackingAccuracyReportContainer";
import LatestIncidentsContainer from "../containers/LatestIncidentsContainer";
import AAATrackingAccuracyReportContainer from "../containers/AAATrackingAccuracyReportContainer";
import AATrackingAccuracyReportContainer from "../containers/AATrackingAccuracyReportContainer";
import HighATrackingAccuracyReportContainer from "../containers/HighATrackingAccuracyReportContainer";
import ATrackingAccuracyReportContainer from "../containers/ATrackingAccuracyReportContainer";
import RookieTrackingAccuracyReportContainer from "../containers/RookieTrackingAccuracyReportContainer";
import AmateurTrackingAccuracyReportContainer from "../containers/AmateurTrackingAccuracyReportContainer";
import WinterTrackingAccuracyReportContainer from "../containers/WinterTrackingAccuracyReportContainer";
import InternationalIndependentTrackingAccuracyReportContainer from "../containers/InternationalIndependentTrackingAccuracyReportContainer";
import NGETrackingAccuracyReportContainer from "../containers/NGETrackingAccuracyReportContainer";
import moment from "moment-timezone";

class AuthStore {
  constructor(routerStore, oktaAuth) {
    this.routerStore = routerStore;
    this.oktaAuth = oktaAuth;

    const dateParam = this.routerStore.location.search
      ? new URLSearchParams(this.routerStore.location.search).get("date")
      : false;

    const now = moment().tz("America/New_York");
    this.defaults = {
      date: dateParam
        ? dateParam
        : (now.hour() < 10 ? now.subtract(1, "day") : now).format("YYYY-MM-DD"),
      selectedPitches: [],
    };

    this.routerStore.setDate(
      this.routerStore.date ? this.routerStore.date : this.defaults["date"],
    );

    extendObservable(this, {
      isAuthenticated: false,
      isGettingUser: false,
      error: {
        isVisible: false,
        message: "",
      },
      user: null,
      username: computed(() => (this.user ? this.user.email : null)),
      groups: computed(() => (this.user ? this.user.groups : null)),
      hasIncidentEdit: computed(
        () => this.user && _.includes(this.user.groups, Groups.ADMIN),
      ),
      isAdmin: computed(
        () => this.user && _.includes(this.user.groups, Groups.ADMIN),
      ),
      isAdminOrSuperUser: computed(
        () =>
          this.user &&
          (_.includes(this.user.groups, Groups.ADMIN) ||
            _.includes(this.user.groups, Groups.SUPER_USER)),
      ),
      logout: action(async () => {
        await this.oktaAuth.signOut({
          revokeAccessToken: false,
          revokeRefreshToken: false,
          clearTokensBeforeRedirect: true,
          postLogoutRedirectUri: `${window.location.origin}${RouteConstants.BASE}`,
        });
      }),
      pages: computed(() => [
        {
          path: RouteConstants.BASE,
          label: "MLB",
          isProtected: true,
          isActive: this.routerStore.isMLBTrackingAccuracyReportTab,
          component: MLBTrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.NON_MLB_STATCAST,
          label: "Non-MLB Statcast",
          isProtected: true,
          isActive: this.routerStore.isNonMLBTrackingAccuracyReportTab,
          component: NonMLBTrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },

        {
          path: RouteConstants.AAA_TRACKING,
          label: "AAA",
          isProtected: true,
          isActive: this.routerStore.isAAATrackingAccuracyReportTab,
          component: AAATrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.AA_TRACKING,
          label: "AA",
          isProtected: true,
          isActive: this.routerStore.isAATrackingAccuracyReportTab,
          component: AATrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.HighA_TRACKING,
          label: "High-A",
          isProtected: true,
          isActive: this.routerStore.isHighATrackingAccuracyReportTab,
          component: HighATrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.A_TRACKING,
          label: "A",
          isProtected: true,
          isActive: this.routerStore.isATrackingAccuracyReportTab,
          component: ATrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.ROOKIE_TRACKING,
          label: "Rookie",
          isProtected: true,
          isActive: this.routerStore.isRookieTrackingAccuracyReportTab,
          component: RookieTrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.AMATEUR_TRACKING,
          label: "Amateur",
          isProtected: true,
          isActive: this.routerStore.isAmateurTrackingAccuracyReportTab,
          component: AmateurTrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.WINTER_TRACKING,
          label: "Winter",
          isProtected: true,
          isActive: this.routerStore.isWinterTrackingAccuracyReportTab,
          component: WinterTrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.INDEPENDENT_INTERNATIONAL_TRACKING,
          label: "Int & Indy",
          isProtected: true,
          isActive:
            this.routerStore
              .isInternationalIndependentTrackingAccuracyReportTab,
          component: InternationalIndependentTrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.NGE_TRACKING,
          label: "Non-Game Events",
          isProtected: true,
          isActive: this.routerStore.isNGETrackingAccuracyReportTab,
          component: NGETrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.MILB_TRACKING,
          label: "MiLB (Video Only)",
          isProtected: true,
          isActive: this.routerStore.isMILBTrackingAccuracyReportTab,
          component: MILBTrackingAccuracyReportContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
        {
          path: RouteConstants.LATEST_INCIDENTS,
          label: "Latest Incidents",
          isProtected: true,
          isActive: this.routerStore.isLatestIncidentsTab,
          component: LatestIncidentsContainer,
          isExact: true,
          isHiddenOnNav: false,
        },
      ]),
      checkAuthentication: action(async () => {
        const isAuthenticated = await this.oktaAuth.isAuthenticated();
        if (isAuthenticated !== this.isAuthenticated) {
          this.isAuthenticated = isAuthenticated;
        }
      }),
      getUser: action(async () => {
        this.isGettingUser = true;
        try {
          await this.checkAuthentication();
          if (this.isAuthenticated) {
            const authState = this.oktaAuth.authStateManager.getAuthState();
            const idToken =
              authState?.idToken ||
              (await this.oktaAuth.tokenManager.get("idToken"));
            this.user = idToken?.claims || null;
            if (process.env.NODE_ENV === "development") {
              console.log("Okta user claims", this.user);
            }
          }
          this.isGettingUser = false;
          return this.user;
        } catch (e) {
          window.localStorage.clear();
          this.isGettingUser = false;
        }
      }),
    });

    oktaAuth.authStateManager.subscribe((authState) => {
      runInAction(() => {
        this.isAuthenticated = !!authState?.isAuthenticated;
        if (authState?.isAuthenticated && authState.idToken) {
          this.user = authState.idToken.claims;
          this.isGettingUser = false;
        } else if (!authState?.isAuthenticated) {
          this.user = null;
        }
      });
    });
  }
}

export default AuthStore;
